import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, Plus, Edit, Trash2, Mail, X, Clock } from "lucide-react";

const SellerNotificationSetup = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    action_type: "",
    is_enabled: true,
    recipient_emails: [],
    schedule_enabled: false,
    schedule_frequency: "hourly",
    schedule_time: "09:00"
  });
  
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [actionsRes, configsRes] = await Promise.all([
        axios.get(`${API}/notification-config/seller/actions`),
        axios.get(`${API}/notification-config/seller`)
      ]);
      
      setActions(actionsRes.data.actions);
      setConfigs(configsRes.data.configs);
    } catch (error) {
      toast.error("Failed to fetch notification settings");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        action_type: config.action_type,
        is_enabled: config.is_enabled,
        recipient_emails: [...config.recipient_emails],
        schedule_enabled: config.schedule_enabled || false,
        schedule_frequency: config.schedule_frequency || "hourly",
        schedule_time: config.schedule_time || "09:00"
      });
    } else {
      setEditingConfig(null);
      setFormData({
        action_type: "",
        is_enabled: true,
        recipient_emails: [],
        schedule_enabled: false,
        schedule_frequency: "hourly",
        schedule_time: "09:00"
      });
    }
    setEmailInput("");
    setShowDialog(true);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    if (formData.recipient_emails.includes(email)) {
      toast.error("Email already added");
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      recipient_emails: [...prev.recipient_emails, email]
    }));
    setEmailInput("");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      recipient_emails: prev.recipient_emails.filter(e => e !== emailToRemove)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.action_type) {
      toast.error("Please select an action type");
      return;
    }
    
    if (formData.recipient_emails.length === 0) {
      toast.error("Please add at least one recipient email");
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingConfig) {
        await axios.put(
          `${API}/notification-config/seller/${editingConfig.action_type}`,
          formData
        );
        toast.success("Notification configuration updated");
      } else {
        await axios.post(
          `${API}/notification-config/seller`,
          formData
        );
        toast.success("Notification configuration created");
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (config) => {
    if (!confirm("Are you sure you want to delete this notification configuration?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/notification-config/seller/${config.action_type}`);
      toast.success("Configuration deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete configuration");
      console.error(error);
    }
  };

  const getActionLabel = (actionValue) => {
    const action = actions.find(a => a.value === actionValue);
    return action?.label || actionValue;
  };

  const getCurrentAction = () => {
    return actions.find(a => a.value === formData.action_type);
  };

  const supportsSchedule = () => {
    const action = getCurrentAction();
    return action?.supports_schedule || false;
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-8 h-8 text-teal-600" />
              Email Notifications Setup
            </h1>
            <p className="text-gray-600 mt-1">Configure email notifications for important events</p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Configuration
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading configurations...</p>
          </div>
        ) : configs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No notification configurations yet</p>
              <Button onClick={() => handleOpenDialog()} className="bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />
                Create First Configuration
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {configs.map((config) => (
              <Card key={config.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-lg">{getActionLabel(config.action_type)}</CardTitle>
                        <Badge variant={config.is_enabled ? "default" : "secondary"} className={config.is_enabled ? "bg-green-500" : ""}>
                          {config.is_enabled ? "Enabled" : "Disabled"}
                        </Badge>
                        {config.schedule_enabled && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Clock className="w-3 h-3 mr-1" />
                            Scheduled
                          </Badge>
                        )}
                      </div>
                      <CardDescription>
                        {actions.find(a => a.value === config.action_type)?.description || ""}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleOpenDialog(config)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(config)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm text-gray-600">Recipients ({config.recipient_emails.length})</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {config.recipient_emails.map((email, idx) => (
                          <Badge key={idx} variant="secondary" className="bg-teal-50 text-teal-700">
                            <Mail className="w-3 h-3 mr-1" />
                            {email}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {config.schedule_enabled && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <Label className="text-sm text-blue-900 font-medium">Schedule Configuration</Label>
                        <div className="mt-2 text-sm text-blue-800">
                          <p>
                            <strong>Frequency:</strong>{" "}
                            {config.schedule_frequency === "hourly" && "Every hour"}
                            {config.schedule_frequency === "every_6_hours" && "Every 6 hours"}
                            {config.schedule_frequency === "every_12_hours" && "Every 12 hours"}
                            {config.schedule_frequency === "daily" && `Daily at ${config.schedule_time || "09:00"}`}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingConfig ? "Edit" : "Create"} Notification Configuration</DialogTitle>
              <DialogDescription>
                Configure email notifications for specific actions
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div>
                <Label>Action Type *</Label>
                <Select
                  value={formData.action_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, action_type: value }))}
                  disabled={editingConfig !== null}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select action type" />
                  </SelectTrigger>
                  <SelectContent>
                    {actions.map((action) => (
                      <SelectItem key={action.value} value={action.value}>
                        <div>
                          <div className="font-medium">{action.label}</div>
                          <div className="text-xs text-gray-500">{action.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Notification</Label>
                  <p className="text-sm text-gray-500">Turn on/off this notification</p>
                </div>
                <Switch
                  checked={formData.is_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_enabled: checked }))}
                />
              </div>

              {supportsSchedule() && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label>Enable Scheduled Checks</Label>
                      <p className="text-sm text-gray-500">Run periodic checks for this notification</p>
                    </div>
                    <Switch
                      checked={formData.schedule_enabled}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, schedule_enabled: checked }))}
                    />
                  </div>

                  {formData.schedule_enabled && (
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Check Frequency *</Label>
                        <Select
                          value={formData.schedule_frequency}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, schedule_frequency: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hourly">Every hour</SelectItem>
                            <SelectItem value="every_6_hours">Every 6 hours</SelectItem>
                            <SelectItem value="every_12_hours">Every 12 hours</SelectItem>
                            <SelectItem value="daily">Daily</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.schedule_frequency === "daily" && (
                        <div>
                          <Label>Time (24-hour format) *</Label>
                          <Input
                            type="time"
                            value={formData.schedule_time}
                            onChange={(e) => setFormData(prev => ({ ...prev, schedule_time: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label>Recipient Emails *</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
                  />
                  <Button type="button" onClick={handleAddEmail} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {formData.recipient_emails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.recipient_emails.map((email, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-teal-50 text-teal-700">
                        <Mail className="w-3 h-3 mr-1" />
                        {email}
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          className="ml-2 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-teal-600 hover:bg-teal-700">
                {submitting ? "Saving..." : (editingConfig ? "Update" : "Create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SellerLayout>
  );
};

export default SellerNotificationSetup;